import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.function.name.js";
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { fillParent } from 'styles/mixins';
import { BASIC } from 'styles/spacingPresets';
import { getColoredBackgroundShade, hasReactChildren, pxToRem } from 'utils/ui';
import Box from 'components/Box';
import Button from 'components/Button';
import ButtonCore from 'components/ButtonCore';
import { Checkbox } from 'components/CheckboxOption';
import Flex from 'components/Flex';
import Heading from 'components/Heading';
import Hidden from 'components/Hidden';
import Stack from 'components/Stack';
import UserGeneratedText from 'components/UserGeneratedText';
import VisuallyHidden from 'components/VisuallyHidden';
import { Ranking } from '../shared';
import { useSelectableCardHandlers, SingleSelectButtonFocusWrapper } from '../SelectableContentCard';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function SelectableSectionWrapper(_ref) {
  var isSelected = _ref.isSelected,
    children = _ref.children;
  return /*#__PURE__*/_jsx(Box, {
    position: "relative",
    pb: "s",
    pr: "s",
    borderRadius: "large",
    backgroundColor: function backgroundColor(theme) {
      return isSelected ? getColoredBackgroundShade(theme) : undefined;
    },
    children: children
  });
}
var SectionCard = styled(Flex).withConfig({
  displayName: "SelectableSection__SectionCard",
  componentId: "sc-1bpl4qu-0"
})(["&::after{content:'';", " pointer-events:none;border:1px solid ", ";border-radius:", ";opacity:0;transition:none;}&:hover::after,&[data-selected='true']::after{opacity:1;transition:opacity 0.1s linear 0.25s;}&[data-selected='true']::after{transition-delay:0s;box-shadow:", " 0 0 0 ", ";}"], fillParent, function (p) {
  return p.theme.links;
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.large);
}, pxToRem(-3), function (p) {
  return p.theme.links;
});
export function SelectableSection(_ref2) {
  var isSelected = _ref2.isSelected,
    isSelectionDisabled = _ref2.isSelectionDisabled,
    isSectionEditable = _ref2.isSectionEditable,
    onToggleSelection = _ref2.onToggleSelection,
    onClick = _ref2.onClick,
    onMouseOver = _ref2.onMouseOver,
    onRequestEdit = _ref2.onRequestEdit,
    name = _ref2.name,
    description = _ref2.description,
    number = _ref2.number,
    contentInfo = _ref2.contentInfo,
    hasPlaylistNumbering = _ref2.hasPlaylistNumbering;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  function toggleSelection() {
    onToggleSelection(!isSelected);
  }
  var _useSelectableCardHan = useSelectableCardHandlers({
      onClick: onClick,
      toggleSelection: toggleSelection
    }),
    checkboxRef = _useSelectableCardHan.checkboxRef,
    checkboxContainerRef = _useSelectableCardHan.checkboxContainerRef,
    handleCheckboxContainerClick = _useSelectableCardHan.handleCheckboxContainerClick,
    handleCardClick = _useSelectableCardHan.handleCardClick;
  var titleId = "".concat(number, "-title");
  return /*#__PURE__*/_jsxs(SectionCard, {
    forwardedAs: "article",
    "aria-labelledby": titleId,
    "data-selected": isSelected && !isSelectionDisabled,
    onClick: handleCardClick,
    onMouseOver: onMouseOver,
    cursor: "pointer",
    align: "top",
    children: [/*#__PURE__*/_jsx(Box, {
      ref: checkboxContainerRef,
      onClick: handleCheckboxContainerClick,
      flexAlign: "stretch",
      z: function z(theme) {
        return theme.globals.z.raised + 1;
      },
      pt: 17,
      px: BASIC.SMALL,
      breakpoints: BASIC.BREAKPOINTS,
      cursor: "default",
      children: /*#__PURE__*/_jsx(Checkbox, {
        ref: checkboxRef,
        checked: isSelected,
        onChange: toggleSelection,
        disabled: isSelectionDisabled,
        size: ['small', 'default'],
        breakpoints: "s",
        "aria-labelledby": titleId
      })
    }), /*#__PURE__*/_jsxs(Stack, {
      grow: true,
      shrink: true,
      spacing: "xs",
      pt: "s",
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "baseline",
        gap: "xxs",
        children: [/*#__PURE__*/_jsxs(SingleSelectButtonFocusWrapper, {
          shrink: true,
          children: [/*#__PURE__*/_jsxs(Heading, {
            as: "h3",
            size: "medium",
            children: [hasPlaylistNumbering && /*#__PURE__*/_jsx(Ranking, {
              rank: number
            }), /*#__PURE__*/_jsx("span", {
              id: titleId,
              children: name
            })]
          }), /*#__PURE__*/_jsx(VisuallyHidden, {
            children: /*#__PURE__*/_jsxs(ButtonCore, {
              type: "button",
              onClick: onClick,
              isActive: isSelected,
              children: [t('actions_toggle_single_select'), " ", name]
            })
          })]
        }), isSectionEditable && /*#__PURE__*/_jsx(Hidden, {
          below: "xxs",
          shrink: 0,
          children: /*#__PURE__*/_jsxs(Button, {
            size: "small",
            variant: "transparent",
            onClick: function onClick(e) {
              e.stopPropagation();
              onRequestEdit();
            },
            mt: "-xxs",
            children: [t('actions_edit'), /*#__PURE__*/_jsxs(VisuallyHidden, {
              children: [" ", name]
            })]
          })
        })]
      }), description && /*#__PURE__*/_jsx(Box, {
        fontSize: BASIC.MEDIUM,
        breakpoints: BASIC.BREAKPOINTS,
        children: /*#__PURE__*/_jsx(UserGeneratedText, {
          text: description
        })
      }), contentInfo]
    })]
  });
}
export function SelectableSectionChildren(_ref3) {
  var children = _ref3.children;
  if (!hasReactChildren(children)) {
    return null;
  }
  return /*#__PURE__*/_jsx(Box, {
    pt: "m",
    pl: [20, 34, 46, 49],
    breakpoints: ['xxxs', 'xs', 's'],
    children: children
  });
}